<template>
  <div class="ventrata-calendar-container">
    <client-only>
      <h4 class="calendar-title">
        Choose a Tour Date
      </h4>
      <FunctionalCalendar
        id="productDateCalendar"
        ref="productDateCalendar"
        v-slot="calData"
        v-model="calendarData"
        :is-date-picker="true"
        :date-format="dateFormat.toLowerCase()"
        :limits="limits"
        :sunday-start="true"
        @changedMonth="onMonthChange"
      >
        <div
          class="day-item"
          :class="{
            available: isAvailable(calData.day.date),
            disabled: !isAvailable(calData.day.date),
            selected: curChosenDate === calData.day.date,
            discount: isDiscount(calData.day.date)
          }"
          @click="() => onChoseDay(calData.day)"
        >
          <div class="day-label">
            {{ calData.day.day }}
          </div>
          <div class="day-status-label">
            {{ getPriceLabel(calData.day) }}
          </div>
        </div>
      </FunctionalCalendar>
    </client-only>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getAvailabilityPayload, getAdultPricing, findLowestPriceAvailability, replaceAvailabilitiesById } from '@/helpers/ventrata'
import { getFromPrice, addCurrencySymbol } from '@/helpers/price'
import { getTodayStr, getStartOfMonthStr, getEndOfMonthStr, removeLeadingZeros, addLeadingZeros, formatDate } from '@/helpers/main'

const dateFMT = 'YYYY-MM-DD'

export default {
  name: 'VentrataCalendar',
  props: {
    product: {
      type: Object,
      require: true
    }
  },
  data () {
    return {
      show: false,
      availabilities: null,
      fullAvailabilities: null, // use as local cache
      selectedMonth: null,
      dateFormat: dateFMT,
      calendarData: {},
      curChosenDate: '',
      limits: {
        min: getTodayStr(dateFMT),
        max: ''
      }
    }
  },
  computed: {
    ...mapGetters({
      initAvailabilityList: 'ventrata/initAvailabilities',
      currency: 'currency'
    })
  },
  watch: {
    initAvailabilityList () {
      // currency update will trigger initAvailabilityList change, calendar should re-render
      console.log('VentrataCalendar initAvailabilityList changed this.currency, curChosenDate => ', this.currency, this.curChosenDate)
      this.curChosenDate = null
      this.onInitCalendar()
    }
  },
  mounted () {
    this.$nextTick(() => {
      console.log('VentrataCalendar init calendar this.currency => ', this.currency)
      this.availabilities = this.initAvailabilityList
      this.onInitCalendar()
    })
  },
  methods: {
    onInitCalendar () {
      if (this.curChosenDate) return

      this.availabilities = this.initAvailabilityList?.filter(a => a.available) ?? []
      this.fullAvailabilities = this.availabilities
      // Find first available date using optional chaining and early array methods
      const firstAvail = this.availabilities.find(v => v.available)

      if (firstAvail) {
        const availabilityDate = firstAvail.id.slice(0, 10)
        const firstAvailableDate = removeLeadingZeros(availabilityDate)

        // Set all related properties in one block
        this.curChosenDate = this.selectedMonth = firstAvailableDate

        // Update calendar and emit event
        this.$refs.productDateCalendar.ChooseDate(firstAvailableDate)
        this.$emit('selectDate', firstAvailableDate)
      }
    },
    isAvailable (date) {
      if (!this.availabilities) { return false }
      const targetDate = formatDate(date, dateFMT, dateFMT)

      const availability = this.availabilities
        .filter(item => item.available)
        .find((item) => {
          const availabilityDate = item.id?.slice(0, 10)
          const formattedAvailDate = formatDate(availabilityDate, dateFMT)
          return formattedAvailDate === targetDate
        })

      // utcCutoffAt - availability will be false if current time passed utcCutoffAt
      // Ventrata already handles it
      return availability
    },
    isDiscount (date) {
      if (!this.availabilities) {
        return false
      }
      const item = this.getAvailabilityByDate(date)
      if (!item) {
        return false
      }
      // console.log('isDiscount item :>> ', item)
      // return false
      const adultPricing = getAdultPricing(item)
      const curPricePre = adultPricing?.original
      if (!curPricePre) {
        return false
      }
      const curPrice = curPricePre / 100
      // TODO need to confirm 'dynamic price' setting
      const fromPrice = getFromPrice(this.product, this.currency)

      // console.log('fromPrice :>> ', fromPrice);
      // console.log('curPrice :>> ', curPrice);
      return curPrice < fromPrice
    },
    onChoseDay (dayObj) {
      if(!this.isAvailable(dayObj.date)) {
        return
      }
      this.curChosenDate = dayObj?.date
      this.$emit('selectDate', this.curChosenDate)
    },
    async onMonthChange (newDate) {
      this.availabilities = []
      this.loading = true

      this.selectedMonth = newDate
      const startDate = getStartOfMonthStr(newDate, dateFMT)
      const endDate = getEndOfMonthStr(newDate, dateFMT)
      console.log('date range :>> ', startDate, endDate)
      this.availabilities = this.fullAvailabilities.filter((avail) => {
        const availDate = avail.id?.slice(0, 10)
        return availDate >= startDate && startDate <= endDate
      })

      const availabilityPayload = getAvailabilityPayload(this.product, startDate, endDate, this.currency)

      const res = await this.$store.dispatch('ventrata/getAvailability', availabilityPayload)
      const processedAvailabilities = res?.filter(a => a.available) ?? []
      this.availabilities = processedAvailabilities
      this.fullAvailabilities = replaceAvailabilitiesById(this.fullAvailabilities, processedAvailabilities)
      this.loading = false
    },
    getAvailabilityByDate (date) {
      // need to get the lower price availability
      // some tours have diff price for diff time slots on same day
      const curDayAvailabilities = this.availabilities.filter(i => i.id.slice(0, 10) === addLeadingZeros(date))
      return findLowestPriceAvailability(curDayAvailabilities)
    },
    getPriceLabel (dayObj) {
      if (!this.availabilities) {
        return ''
      }
      const item = this.getAvailabilityByDate(dayObj.date)
      if (!item) {
        return '-'
      }
      if (item.status === 'SOLD_OUT') {
        return '-'
      }
      if (!item.available) {
        return '-'
      }
      // console.log('item => ', item)
      const adultPricing = getAdultPricing(item)
      // const pricingFrom = item.pricingFrom
      return addCurrencySymbol(adultPricing.currency, adultPricing.original / 100)
    }
  }
}
</script>

<style lang="scss" scoped>
.ventrata-calendar-container {
  position: relative;
  width: 100%;
  border-radius: 4px;
  background-color: $text-white;
  padding: 24px;
  overflow: hidden;

  .calendar-title {
    margin: 0;
    @include font__header-5();
  }

  .ventrata-calendar-loading {
    position: absolute;
    inset: 0;
    z-index: 999;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $bg-grey;
  }

  .day-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    &:not(.disabled) {
      .day-label {
        &:hover {
          cursor: pointer;
          color: $text-white !important;
          background-color: $brand-primary-color !important;
        }
      }
    }
    .day-label {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      margin: 0;
      border-radius: 50%;
      @include font__paragraph-1--bold();
      color: #ffffff;
      background-color: transparent;
      transition: background-color 0.3s ease;
    }
    .day-status-label {
      width: 100%;
      margin: 4px 0 0;
      @include font__small-text-1--regular();
      color: $blackberry;
      text-align: center;
      word-break: break-all;
      white-space: nowrap;
    }
    &.available {
      .day-label {
        color: $brand-primary-color;
      }
    }
    &.discount {
      .day-status-label {
        color: $woi-gold-main;
        font-weight: bold;
      }
    }
    &.disabled {
      .day-label {
        background-color: transparent;
        text-decoration-line: line-through;
        font-family: Vollkorn;
        font-weight: 400;
        font-size: 18px;
        line-height: 26px;
        letter-spacing: 0px;
        text-align: center;
        color: $grey-3;
      }
      .day-status-label {
        color: $blueberry-light;
      }
    }
    &.selected {
      .day-label {
        color: $text-white;
        background-color: $woi-gold-main;
      }
      .day-status-label {
        font-weight: bold;
        color: $woi-gold-main;
      }
    }
  }
}

@include max-width($mobile) {
  .ventrata-calendar-container {
    padding: 16px;
    .calendar-title {
      margin: 0;
      @include font__title-1--bold();
    }
  }
}
</style>

<!-- Global Styles -->
<style lang="scss">
$top-date-margin: Min(1rem, 3vh);
#productDateCalendar .vfc-main-container {
  box-shadow: none;
  background-color: transparent;
}
#productDateCalendar .vfc-content {
  margin: 0;
}

#productDateCalendar {
  .vfc-calendars-container {
    .vfc-top-date {
      margin-top: $top-date-margin;
      margin-bottom: $top-date-margin;
      @include font__title-1--bold();
      text-align: center;
      span {
        color: $brand-primary-color;
      }
    }

    .vfc-arrow-left, .vfc-arrow-right {
      width: 8px;
      height: 8px;
      margin-left: 0;
      margin-right: 0;
    }

    .vfc-dayNames span {
      color: $brand-blackberry-light;
    }

    .non-display {
      display: none;
    }

    @extend %paragraph-1;

    .vfc-dayNames {
      padding: 10px 0;
      margin: 0 10px 20px;
      justify-content: space-between;
      border-bottom: 1px solid $grey;
      .vfc-day {
        width: 32px;
        height: 24px;
        @include font__paragraph-1--regular();
        text-align: center;
      }

      & + div {
        & > div:not([class]):last-child {
          height: 0 !important;
        }
      }
    }

    .vfc-week {
      justify-content: space-between;
      padding: 0 10px;
      .vfc-day {
        width: 32px;
        .day-item {
          z-index: 10;

        }
        .vfc-hide {
          .day-item {
            display: none;
          }
        }

        .vfc-hover {
          .day-label:hover {

          }
        }

        .vfc-base-start,
        .vfc-base-end {
          background-color: $grey;
        }
      }
    }
  }
}

@include max-width($mobile-extra) {
  #productDateCalendar .vfc-calendars-container,
  #productDateCalendar .vfc-main-container {

    .vfc-dayNames {
      margin-bottom: 4px;
    }
    .vfc-top-date {
      padding: 4px 0 0;
    }
    .vfc-dayNames {
      .vfc-day {
        @include font__paragraph-1--regular();
        text-align: center;
      }
    }
  }
}
</style>
